import ApplicationController from 'javascript/controllers/application_controller';

const INVENTIVE_EMBED_SHARE_PARAMS = ['chatThreadId'];
const VALID_MODES = ['sidebar', 'fullscreen', 'floating'];
const STORAGE_KEY = 'owl-inventive-display-mode';
const SIDEBAR_WIDTH_STORAGE_KEY = 'owl-inventive-sidebar-width-px';
const DEFAULT_SIDEBAR_WIDTH_PX = 640;
const MOBILE_BREAKPOINT = 720;
const BODY_SIDEBAR_INSET_CLASS = 'inventive-embed-sidebar-inset';

export default class extends ApplicationController {
  static targets = [
    'iframe',
    'open',
    'container',
    'backdrop',
    'header',
    'modeButton',
    'modeMenu',
    'modeTrigger',
    'modeDropdown',
    'modeTriggerIcon',
    'resizeHandle',
  ];

  connect() {
    this.expanded = false;
    this.displayMode = null;
    this.dragging = false;
    this.dragStart = null;
    this.initFromStorage();
    this.initEmbed();
    this.boundOnResize = this.onResize.bind(this);
    this.boundOnHeaderMouseDown = this.onHeaderMouseDown.bind(this);
    this.boundOnMouseMove = this.onDocumentMouseMove.bind(this);
    this.boundOnMouseUp = this.onDocumentMouseUp.bind(this);
    this.boundOnModeMenuPointerDown = this.onModeMenuPointerDown.bind(this);
    this.boundOnModeMenuKeydown = this.onModeMenuKeydown.bind(this);
    this.boundResizePointerMove = this.onResizeSidebarPointerMove.bind(this);
    this.boundResizePointerUp = this.onResizeSidebarPointerUp.bind(this);
    this.modeMenuOpen = false;
    this.resizeSidebarDragging = false;
    this.resizeSidebarPointerId = null;
    this.resizeSidebarStartX = null;
    this.resizeSidebarStartWidth = null;
    this.lastEffectiveMode = null;
    window.addEventListener('resize', this.boundOnResize);
    if (this.hasHeaderTarget) {
      this.headerTarget.addEventListener(
        'mousedown',
        this.boundOnHeaderMouseDown,
      );
    }
  }

  disconnect() {
    document.body.classList.remove(BODY_SIDEBAR_INSET_CLASS);
    document.body.style.removeProperty('--inventive-sidebar-inset');
    this.teardownResizeSidebarDrag();
    window.removeEventListener('resize', this.boundOnResize);
    this.removeDragListeners();
    this.teardownModeMenuListeners();
    if (this.hasHeaderTarget) {
      this.headerTarget.removeEventListener(
        'mousedown',
        this.boundOnHeaderMouseDown,
      );
    }
  }

  onHeaderMouseDown(event) {
    if (this.getEffectiveMode() !== 'floating') return;
    if (event.target.closest('button')) return;
    event.preventDefault();
    this.dragging = true;
    const rect = this.containerTarget.getBoundingClientRect();
    this.dragStart = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      left: rect.left,
      top: rect.top,
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.boundOnMouseMove);
    document.addEventListener('mouseup', this.boundOnMouseUp);
  }

  onDocumentMouseMove(event) {
    if (!this.dragging || !this.dragStart) return;
    const left = Math.max(
      0,
      Math.min(
        window.innerWidth - this.containerTarget.offsetWidth,
        event.clientX - this.dragStart.x,
      ),
    );
    const top = Math.max(
      0,
      Math.min(
        window.innerHeight - this.containerTarget.offsetHeight,
        event.clientY - this.dragStart.y,
      ),
    );
    this.containerTarget.style.left = `${left}px`;
    this.containerTarget.style.top = `${top}px`;
    this.containerTarget.style.right = 'auto';
    this.containerTarget.style.bottom = 'auto';
  }

  onDocumentMouseUp() {
    this.removeDragListeners();
  }

  removeDragListeners() {
    this.dragging = false;
    this.dragStart = null;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this.boundOnMouseMove);
    document.removeEventListener('mouseup', this.boundOnMouseUp);
  }

  onResize() {
    this.closeModeMenu();
    if (this.expanded) {
      this.applyModeClasses();
      if (
        this.getEffectiveMode() === 'sidebar' &&
        window.innerWidth >= MOBILE_BREAKPOINT
      ) {
        const w = this.clampSidebarWidthPx(this.containerTarget.offsetWidth);
        this.containerTarget.style.width = `${w}px`;
        this.syncInventiveSidebarInsetCssVariable();
      }
    } else {
      this.syncSidebarPageInset();
    }
  }

  getDefaultMode() {
    return window.innerWidth >= MOBILE_BREAKPOINT ? 'sidebar' : 'fullscreen';
  }

  getEffectiveMode() {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      return 'fullscreen';
    }
    return this.displayMode || this.getDefaultMode();
  }

  initFromStorage() {
    let stored = null;
    try {
      stored = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // storage unavailable; use default
    }
    const mode = VALID_MODES.includes(stored) ? stored : this.getDefaultMode();
    this.displayMode = mode;
    this.applyModeClasses();
  }

  setMode(event) {
    const mode = event.currentTarget?.dataset?.mode;
    if (!mode || !VALID_MODES.includes(mode)) {
      return;
    }
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      this.displayMode = 'fullscreen';
    } else {
      this.displayMode = mode;
      try {
        sessionStorage.setItem(STORAGE_KEY, mode);
      } catch (e) {
        // ignore storage errors
      }
    }
    this.applyModeClasses();
  }

  applyModeClasses() {
    const container = this.containerTarget;
    const effective = this.getEffectiveMode();
    const prevEffective = this.lastEffectiveMode;
    VALID_MODES.forEach((m) =>
      container.classList.remove(`inventive__container--${m}`),
    );
    container.classList.add(`inventive__container--${effective}`);
    container.style.width = '';
    if (effective === 'sidebar' && window.innerWidth >= MOBILE_BREAKPOINT) {
      const saved = this.getSavedSidebarWidthPx();
      if (saved != null) {
        container.style.width = `${saved}px`;
      }
    }
    if (effective === 'sidebar' && window.innerWidth >= MOBILE_BREAKPOINT) {
      // Pin to the right; clear any left/top from floating drag so it cannot sit on the left
      container.style.left = 'auto';
      container.style.top = '0';
      container.style.right = '0';
      container.style.bottom = '';
    } else if (effective !== 'floating') {
      container.style.left = '';
      container.style.top = '';
      container.style.right = '';
      container.style.bottom = '';
    } else if (prevEffective != null && prevEffective !== 'floating') {
      container.style.left = '';
      container.style.top = '';
      container.style.right = '';
      container.style.bottom = '';
    }
    this.lastEffectiveMode = effective;
    this.updateModeButtons();
    this.syncSidebarPageInset();
  }

  syncSidebarPageInset() {
    const shouldInset =
      this.expanded &&
      this.getEffectiveMode() === 'sidebar' &&
      window.innerWidth >= MOBILE_BREAKPOINT;
    document.body.classList.toggle(BODY_SIDEBAR_INSET_CLASS, shouldInset);
    this.syncInventiveSidebarInsetCssVariable();
  }

  clampSidebarWidthPx(w) {
    const min = 320;
    const max = Math.max(
      min + 40,
      Math.min(1000, Math.floor(window.innerWidth * 0.92)),
    );
    return Math.min(max, Math.max(min, Math.round(w)));
  }

  getSavedSidebarWidthPx() {
    try {
      const raw = sessionStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
      const w = parseInt(raw, 10);
      if (Number.isFinite(w)) {
        return this.clampSidebarWidthPx(w);
      }
    } catch (e) {
      // ignore
    }
    return null;
  }

  measureInventiveSidebarWidthPx() {
    const inline = this.containerTarget.style.width;
    if (inline && inline.endsWith('px')) {
      const n = parseInt(inline, 10);
      if (Number.isFinite(n)) {
        return this.clampSidebarWidthPx(n);
      }
    }
    const saved = this.getSavedSidebarWidthPx();
    if (saved != null) {
      return saved;
    }
    return DEFAULT_SIDEBAR_WIDTH_PX;
  }

  syncInventiveSidebarInsetCssVariable() {
    if (!document.body.classList.contains(BODY_SIDEBAR_INSET_CLASS)) {
      document.body.style.removeProperty('--inventive-sidebar-inset');
      return;
    }
    const w = this.measureInventiveSidebarWidthPx();
    document.body.style.setProperty('--inventive-sidebar-inset', `${w}px`);
  }

  startResizeSidebar(event) {
    if (this.resizeSidebarDragging) return;
    event.preventDefault();
    if (this.getEffectiveMode() !== 'sidebar') return;
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    if (event.button !== undefined && event.button !== 0) return;
    this.resizeSidebarDragging = true;
    this.resizeSidebarPointerId = event.pointerId;
    this.resizeSidebarStartX = event.clientX;
    this.resizeSidebarStartWidth = this.containerTarget.offsetWidth;
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', this.boundResizePointerMove);
    document.addEventListener('pointerup', this.boundResizePointerUp);
    document.addEventListener('pointercancel', this.boundResizePointerUp);
    if (this.hasResizeHandleTarget && event.pointerId != null) {
      try {
        this.resizeHandleTarget.setPointerCapture(event.pointerId);
      } catch (e) {
        // ignore
      }
    }
  }

  onResizeSidebarPointerMove(event) {
    if (!this.resizeSidebarDragging) return;
    // Panel is anchored to the right; moving the left edge left (smaller clientX) widens the panel
    const dx = event.clientX - this.resizeSidebarStartX;
    const next = this.clampSidebarWidthPx(this.resizeSidebarStartWidth - dx);
    this.containerTarget.style.width = `${next}px`;
    this.syncInventiveSidebarInsetCssVariable();
  }

  onResizeSidebarPointerUp() {
    if (!this.resizeSidebarDragging) return;
    this.teardownResizeSidebarDrag();
    try {
      sessionStorage.setItem(
        SIDEBAR_WIDTH_STORAGE_KEY,
        String(this.containerTarget.offsetWidth),
      );
    } catch (e) {
      // ignore
    }
    this.syncInventiveSidebarInsetCssVariable();
  }

  teardownResizeSidebarDrag() {
    if (this.hasResizeHandleTarget && this.resizeSidebarPointerId != null) {
      try {
        this.resizeHandleTarget.releasePointerCapture(
          this.resizeSidebarPointerId,
        );
      } catch (e) {
        // ignore
      }
    }
    this.resizeSidebarPointerId = null;
    this.resizeSidebarDragging = false;
    document.body.style.userSelect = '';
    document.removeEventListener('pointermove', this.boundResizePointerMove);
    document.removeEventListener('pointerup', this.boundResizePointerUp);
    document.removeEventListener('pointercancel', this.boundResizePointerUp);
  }

  toggleModeMenu(event) {
    event.stopPropagation();
    this.modeMenuOpen = !this.modeMenuOpen;
    this.syncModeMenuOpen();
  }

  closeModeMenu() {
    if (!this.modeMenuOpen) return;
    this.modeMenuOpen = false;
    this.syncModeMenuOpen();
  }

  syncModeMenuOpen() {
    if (!this.hasModeDropdownTarget || !this.hasModeTriggerTarget) return;
    const open = this.modeMenuOpen;
    this.modeDropdownTarget.classList.toggle(
      'inventive__mode-dropdown--open',
      open,
    );
    this.modeTriggerTarget.setAttribute(
      'aria-expanded',
      open ? 'true' : 'false',
    );
    this.modeTriggerTarget.classList.toggle(
      'inventive__mode-trigger--open',
      open,
    );
    if (open) {
      this.teardownModeMenuListeners();
      document.addEventListener(
        'pointerdown',
        this.boundOnModeMenuPointerDown,
        true,
      );
      document.addEventListener('keydown', this.boundOnModeMenuKeydown, true);
    } else {
      this.teardownModeMenuListeners();
    }
  }

  teardownModeMenuListeners() {
    document.removeEventListener(
      'pointerdown',
      this.boundOnModeMenuPointerDown,
      true,
    );
    document.removeEventListener('keydown', this.boundOnModeMenuKeydown, true);
  }

  onModeMenuPointerDown(event) {
    if (!this.modeMenuOpen || !this.hasModeMenuTarget) return;
    if (this.modeMenuTarget.contains(event.target)) return;
    this.modeMenuOpen = false;
    this.syncModeMenuOpen();
  }

  onModeMenuKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.closeModeMenu();
  }

  updateModeButtons() {
    const effective = this.getEffectiveMode();
    if (this.hasModeTriggerIconTarget) {
      this.modeTriggerIconTargets.forEach((el) => {
        const mode = el.dataset.mode;
        el.classList.toggle(
          'inventive__mode-trigger-icon--current',
          mode === effective,
        );
      });
    }
    if (!this.hasModeButtonTarget) return;
    this.modeButtonTargets.forEach((btn) => {
      const mode = btn.dataset.mode;
      const active = mode === effective;
      btn.classList.toggle('inventive__mode-dropdown-option--active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });
  }

  getOpenWidth() {
    const mode = this.getEffectiveMode();
    if (mode === 'fullscreen') return '100%';
    if (mode === 'floating') return 435;
    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      return this.getSavedSidebarWidthPx() ?? DEFAULT_SIDEBAR_WIDTH_PX;
    }
    return '100%';
  }

  open() {
    if (this.expanded) {
      return;
    }

    this.expanded = true;
    this.syncSidebarPageInset();

    $(this.openTarget).addClass('inventive__button--disabled');
    this.containerTarget.classList.remove('inventive__container--hidden');
    this.containerTarget.style.width = '0px';
    this.containerTarget.style.overflow = 'hidden';
    this.containerTarget.style.display = '';
    const isFloating = this.getEffectiveMode() === 'floating';
    if (isFloating) {
      this.containerTarget.style.minWidth = '0px';
    }
    $(this.openTarget).fadeOut();
    const targetWidth = this.getOpenWidth();
    // Force reflow so browser processes display/width before animating
    void this.containerTarget.offsetHeight;
    $(this.containerTarget).animate(
      { width: targetWidth },
      {
        duration: 300,
        complete: () => {
          this.containerTarget.style.overflow = '';
          if (isFloating) {
            this.containerTarget.style.minWidth = '';
          }
          this.syncInventiveSidebarInsetCssVariable();
        },
      },
    );
  }

  close() {
    if (!this.expanded) {
      return;
    }

    this.expanded = false;
    this.syncSidebarPageInset();

    $(this.openTarget).addClass('inventive__button--disabled');
    const isFloating = this.getEffectiveMode() === 'floating';
    if (isFloating) {
      this.containerTarget.style.minWidth = '0';
    }
    $(this.containerTarget).animate(
      { width: 0 },
      {
        duration: 300,
        complete: () => {
          if (isFloating) {
            this.containerTarget.style.minWidth = '';
          }
          this.containerTarget.classList.add('inventive__container--hidden');
          this.containerTarget.style.width = '0px';
          $(this.openTarget).fadeIn();
        },
      },
    );
    $(this.openTarget).removeClass('inventive__button--disabled');
  }

  initEmbed() {
    let path = this.data.get('proxy-path');
    let search = location.search.substring(1);

    if (search.length > 0) {
      path += (path.includes('?') ? '&' : '?') + search;
    }

    fetch(path)
      .then((response) => response.json())
      .then((data) => {
        if (!data.url) {
          return;
        }

        $(this.openTarget).fadeIn();

        this.iframeTarget.src = data.url;
        window.onmessage = (event) => {
          const targetOrigin = new URL(data.url).origin;

          if (event.origin !== targetOrigin) {
            // ignore messages from other origins
            return;
          }
          const { type } = event.data;
          if (type !== 'embed_content_ready') {
            // ignore messages with other types
            return;
          }

          const embedTokensMsg = {
            type: 'embed_tokens',
            tokens: data.tokens,
            scopeToken: data.scopeToken,
            hostUrl: location.origin + location.pathname + location.hash,
          };
          this.iframeTarget.contentWindow.postMessage(
            embedTokensMsg,
            targetOrigin,
          );

          window.onmessage = null;
        };

        setTimeout(() => {
          this.autoOpen();
        });
      })
      .catch((error) => console.error('Error fetching iframe URL:', error));
  }

  autoOpen() {
    const urlParams = new URLSearchParams(window.location.search);

    if (INVENTIVE_EMBED_SHARE_PARAMS.some((param) => urlParams.has(param))) {
      this.open();
    }
  }
}
